import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, TextField, ThemeProvider, CssBaseline, CircularProgress } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { theme } from '../theme/theme';
import { postCodeRegister, CodigoResgiser } from '../services/auth_request';

const validationSchema = yup.object({
  code: yup
    .string()
    .matches(/^[0-9]{6}$/, 'El código debe ser de 6 dígitos numéricos')
    .required('El código es requerido'),
});

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state && location.state.email) {
      setEmail(location.state.email);
    } else {
      // Manejar el caso donde no se recibe el email (ej. acceso directo a la URL)
      alert('No se ha proporcionado un email para verificar. Por favor, regístrate primero.');
      navigate('/register'); // Redirigir al registro si no hay email
    }
  }, [location, navigate]);

  const formik = useFormik({
    initialValues: {
      code: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      if (!email) {
        alert('Error: No se ha encontrado el email para la verificación.');
        setLoading(false);
        return;
      }

      const verificationData: CodigoResgiser = {
        email: email,
        codigo: Number(values.code),
      };

      try {
        const status = await postCodeRegister(verificationData);
        if (status === 201 || status === 200) {
          alert('Correo verificado exitosamente. Redirigiendo a inicio de sesión.');
          navigate('/login');
        } else {
          alert(`Error en la verificación. Código: ${status}`);
        }
      } catch (error) {
        alert('Ocurrió un error al intentar verificar el correo.');
        console.error('Error de verificación:', error);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: theme.palette.text.primary }}>
          Verificación de Correo
        </Typography>
        <Typography variant="body1" mb={3} sx={{ color: theme.palette.text.secondary }}>
          Hemos enviado un código de 6 dígitos a tu correo electrónico: <strong>{email}</strong>. Por favor, ingrésalo a continuación para verificar tu cuenta.
        </Typography>
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              id="code"
              name="code"
              label="Código de Verificación"
              value={formik.values.code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.code && Boolean(formik.errors.code)}
              helperText={formik.touched.code && formik.errors.code}
              sx={{ mb: 3 }}
            />
            <Button color="primary" variant="contained" fullWidth type="submit" disabled={loading}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verificar Correo'}
            </Button>
          </form>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default VerifyEmail;
